import { useEffect, useState } from 'react';
import { onSupabaseAuthStateChange } from '@/shared/clients/supabaseClient';
import { isRealtimeEnabled } from '@/shared/lib/env';
import {
  getCurrentFamilyId,
  getCurrentProfileId,
  subscribeActiveFamilyChange,
} from '@/shared/lib/familyContext';

/**
 * Resolved realtime scoping identifiers (P7).
 *
 * Both ids are `null` until resolved. They stay `null` forever when realtime is
 * disabled, which keeps the dependent subscriptions inert.
 */
export interface RealtimeScope {
  readonly familyId: string | null;
  readonly profileId: string | null;
}

/** The unresolved scope — every dependent subscription is gated off. */
const EMPTY_SCOPE: RealtimeScope = { familyId: null, profileId: null };

/**
 * Backoff schedule for a failed resolve, in milliseconds. Bounded on purpose:
 * after the last entry the hook stops retrying on its own and waits for an
 * external trigger (`online`, an auth-state change, a family switch), so a
 * signed-out tab does not poll GoTrue for the rest of its life.
 */
export const RESOLVE_RETRY_DELAYS_MS = [1_000, 2_000, 5_000, 15_000] as const;

/**
 * How long a family switch keeps the scope blank before re-resolving.
 *
 * Two things depend on the gap. First, `familyStore.switchFamily` fires a
 * fire-and-forget `disconnectAllRealtime()` (a dynamic import away), so a
 * channel re-created immediately is removed by that teardown a tick later and
 * never comes back. Second, the notifications channel is keyed on
 * `profile_id=eq.<id>`, which a switch does not change — blanking the scope is
 * what flips its `enabled` gate off and on, and therefore the only thing that
 * makes {@link import('./useSupabaseRealtime').useSupabaseRealtime} re-run its
 * effect for that table.
 *
 * Same value and same reasoning as `RESUBSCRIBE_DELAY_MS` in
 * `useSupabaseFamilyPresence`.
 */
export const FAMILY_SWITCH_SETTLE_MS = 1_000;

/**
 * Resolve the current `family_id` / `profile_id` for realtime subscription
 * filters. Only touches Supabase when realtime is enabled
 * ({@link isRealtimeEnabled}); with the flag off it is a no-op returning nulls,
 * so nothing connects.
 *
 * Resolution is retried rather than attempted once: a single transient failure
 * (a network blip during boot, an expired token mid-refresh) used to leave the
 * scope `null` for the rest of the session, which silently disabled every
 * realtime subscription AND presence with nothing surfaced to the user. The
 * hook now retries with bounded backoff and re-resolves on the window `online`
 * event and on Supabase auth-state changes.
 *
 * @module shared/hooks/useRealtimeScope
 */
export function useRealtimeScope(): RealtimeScope {
  const [scope, setScope] = useState<RealtimeScope>(EMPTY_SCOPE);

  useEffect(() => {
    if (!isRealtimeEnabled) {
      return;
    }

    let cancelled = false;
    /** Mirrors the last scope handed to React, readable from the listeners. */
    let current: RealtimeScope = EMPTY_SCOPE;
    /** Index into {@link RESOLVE_RETRY_DELAYS_MS}. */
    let attempt = 0;
    /** Pending retry or family-switch settle timer. */
    let timer: ReturnType<typeof setTimeout> | null = null;
    /** True while the family-switch settle delay is pending. */
    let settling = false;
    /** True while a resolve is awaiting its round trips. */
    let inFlight = false;
    /**
     * An external trigger arrived while a resolve was already pending.
     *
     * Remembered rather than discarded. Dropping it left the scope
     * permanently `{null, null}`: an `online` event landing during the LAST
     * backoff attempt was swallowed, that attempt then failed, and
     * `RESOLVE_RETRY_DELAYS_MS[4]` is `undefined`, so no further retry was
     * scheduled and no trigger remained to ask for one.
     */
    let pendingResolve = false;
    /**
     * Bumped whenever the identity underneath a resolve changes (family
     * switch, sign-out). A resolve started before the bump lands on the OLD
     * family — applying it would undo the blank scope, and the notifications
     * channel would then move `{A,P}` → `{B,P}`, which is no identity change
     * at all and therefore no re-subscribe.
     */
    let generation = 0;

    const clearTimer = (): void => {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
    };

    const apply = (next: RealtimeScope): void => {
      current = next;
      // Bail out of no-op renders: a retry or an auth-state change that
      // re-resolves the same ids must not re-render every realtime consumer.
      setScope((previous) =>
        previous.familyId === next.familyId &&
        previous.profileId === next.profileId
          ? previous
          : next
      );
    };

    const resolve = async (): Promise<void> => {
      clearTimer();
      settling = false;
      inFlight = true;
      // This run satisfies any request made before it started; only one made
      // DURING it needs another pass.
      pendingResolve = false;
      const startedAt = generation;
      try {
        const [familyId, profileId] = await Promise.all([
          getCurrentFamilyId(),
          getCurrentProfileId(),
        ]);
        if (cancelled || startedAt !== generation) {
          return;
        }
        attempt = 0;
        apply({ familyId, profileId });
      } catch {
        // No active session (or family) yet, or the round-trip failed. Leave
        // the scope null so the subscriptions stay disabled, and schedule the
        // next attempt — giving up here costs the session all live sync.
        if (cancelled || startedAt !== generation) {
          return;
        }
        apply(EMPTY_SCOPE);
        const delay = RESOLVE_RETRY_DELAYS_MS[attempt];
        if (delay === undefined) {
          return;
        }
        attempt += 1;
        timer = setTimeout(() => {
          void resolve();
        }, delay);
      } finally {
        if (startedAt === generation) {
          inFlight = false;
          if (pendingResolve && !cancelled) {
            // A trigger landed while this resolve was on the wire. Re-enter
            // rather than drop it — `resolve` starts with `clearTimer()`, so
            // the retry this run may just have scheduled is superseded rather
            // than duplicated.
            pendingResolve = false;
            attempt = 0;
            void resolve();
          }
        }
      }
    };

    /** Resolve again from a clean backoff — used by the external triggers. */
    const retryNow = (): void => {
      attempt = 0;
      if (settling || inFlight) {
        // A family switch is already scheduled, or a resolve is already on the
        // wire. Record the request instead of discarding it: the pending run
        // may be resolving the identity the user has just left, and it may be
        // the last one the bounded backoff will ever schedule. Auth emits
        // several events at boot, and collapsing them into one extra pass is
        // what keeps that cheap.
        pendingResolve = true;
        return;
      }
      void resolve();
    };

    /**
     * Blank the scope, discard anything in flight, and re-resolve after the
     * settle delay. Used by both family-switch paths.
     */
    const settleThenResolve = (): void => {
      clearTimer();
      attempt = 0;
      settling = true;
      generation += 1;
      inFlight = false;
      pendingResolve = false;
      apply(EMPTY_SCOPE);
      timer = setTimeout(() => {
        void resolve();
      }, FAMILY_SWITCH_SETTLE_MS);
    };

    void resolve();

    const unsubscribeFamily = subscribeActiveFamilyChange((familyId) => {
      if (current.familyId !== null && familyId !== current.familyId) {
        // A real switch away from a scope channels are subscribed to. Blank it
        // first so every channel is torn down, then re-resolve once the store's
        // own realtime teardown has settled.
        settleThenResolve();
        return;
      }
      if (current.familyId === null && inFlight) {
        // The scope is already blank (an earlier switch) and a resolve is on
        // the wire — one started for the family the user has just left. Letting
        // it land pins every channel and the presence topic to the PREVIOUS
        // family while the app renders the new one, with both ids non-null so
        // no trigger ever repairs it. Discard it via the generation counter and
        // settle again from the new override.
        settleThenResolve();
        return;
      }
      // Nothing is subscribed to the old scope (boot-time override), or the
      // notification is redundant — resolve straight away.
      retryNow();
    });

    // Connectivity came back — repair a scope that never resolved. A resolved
    // scope needs nothing: the ids do not change with connectivity.
    const handleOnline = (): void => {
      if (current.familyId === null || current.profileId === null) {
        retryNow();
      }
    };
    window.addEventListener('online', handleOnline);

    const unsubscribeAuth = onSupabaseAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        clearTimer();
        settling = false;
        attempt = 0;
        generation += 1;
        inFlight = false;
        pendingResolve = false;
        apply(EMPTY_SCOPE);
        return;
      }
      // A recovered session (sign-in, refreshed token) can repair a scope that
      // failed to resolve; an already resolved one is left alone.
      if (current.familyId === null || current.profileId === null) {
        retryNow();
      }
    });

    return () => {
      cancelled = true;
      clearTimer();
      unsubscribeFamily();
      window.removeEventListener('online', handleOnline);
      unsubscribeAuth();
    };
  }, []);

  return scope;
}
